import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormHelperText
} from '@mui/material';
import { customersApi } from 'src/api/endpoints/customers';

interface CreateTransactionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
}

const CreateTransactionModal: React.FC<CreateTransactionModalProps> = ({
  open,
  onClose,
  onSuccess,
  userId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    from_user_id: userId,
    to_user_id: '',
    benif_bank_id: '',
    amount: ''
  });

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate form data
      if (!formData.to_user_id || !formData.benif_bank_id || !formData.amount) {
        setError('Please fill in all required fields');
        return;
      }

      const response = await customersApi.createTransaction({
        from_user_id: formData.from_user_id,
        to_user_id: formData.to_user_id,
        benif_bank_id: formData.benif_bank_id,
        amount: parseFloat(formData.amount)
      });

      if (response.status) {
        onSuccess();
        onClose();
      } else {
        setError(response.message || 'Failed to create transaction');
      }
    } catch (err) {
      setError('An error occurred while creating the transaction');
      console.error('Error creating transaction:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create Transaction</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label="From User ID"
            value={formData.from_user_id}
            onChange={handleChange('from_user_id')}
            fullWidth
            required
          
          />

          <TextField
            label="To User ID"
            value={formData.to_user_id}
            onChange={handleChange('to_user_id')}
            fullWidth
            required
            error={!!error && !formData.to_user_id}
            helperText={error && !formData.to_user_id ? 'To User ID is required' : ''}
          />

          <TextField
            label="Bank ID"
            value={formData.benif_bank_id}
            onChange={handleChange('benif_bank_id')}
            fullWidth
            required
            error={!!error && !formData.benif_bank_id}
            helperText={error && !formData.benif_bank_id ? 'Bank ID is required' : ''}
          />

          <TextField
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={handleChange('amount')}
            fullWidth
            required
            error={!!error && !formData.amount}
            helperText={error && !formData.amount ? 'Amount is required' : ''}
          />

          {error && (
            <FormHelperText error>
              {error}
            </FormHelperText>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Transaction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTransactionModal; 